// src/api/notification-api.js
import { gql } from '@apollo/client';
import client from './apollo-client';

// ================================
// NOTIFICATION QUERIES
// ================================

// Get notifications for admin
export const GET_ADMIN_NOTIFICATIONS = gql`
  query GetAdminNotifications($limit: Int, $offset: Int, $filter: String) {
    adminNotifications(limit: $limit, offset: $offset, filter: $filter) {
      id
      notificationType
      message
      date
      seen
      user {
        id
        firstName
        lastName
        email
        userType
      }
      createdAt
      updatedAt
    }
  }
`;

// Get notifications for laboratory
export const GET_LABORATORY_NOTIFICATIONS = gql`
  query GetLaboratoryNotifications($laboratoryId: ID!, $limit: Int, $offset: Int, $filter: String) {
    laboratoryNotifications(laboratoryId: $laboratoryId, limit: $limit, offset: $offset, filter: $filter) {
      id
      notificationType
      message
      date
      seen
      user {
        id
        firstName
        lastName
        email
      }
      createdAt
      updatedAt
    }
  }
`;

// Get user notifications
export const GET_USER_NOTIFICATIONS = gql`
  query GetUserNotifications($userId: ID!, $limit: Int, $offset: Int, $filter: String) {
    userNotifications(userId: $userId, limit: $limit, offset: $offset, filter: $filter) {
      id
      notificationType
      message
      date
      seen
      relatedId
      createdAt
      updatedAt
    }
  }
`;

// Get notification counts
export const GET_NOTIFICATION_COUNTS = gql`
  query GetNotificationCounts($userId: ID!) {
    notificationCounts(userId: $userId) {
      total
      unread
      thisWeek
    }
  }
`;

// ================================
// NOTIFICATION MUTATIONS
// ================================

// Mark notification as seen
export const MARK_NOTIFICATION_SEEN = gql`
  mutation MarkNotificationSeen($notificationId: ID!) {
    markNotificationSeen(notificationId: $notificationId) {
      id
      seen
      updatedAt
    }
  }
`;

// Mark all notifications as seen
export const MARK_ALL_NOTIFICATIONS_SEEN = gql`
  mutation MarkAllNotificationsSeen($userId: ID!) {
    markAllNotificationsSeen(userId: $userId)
  }
`;

// Broadcast new promotion to all PHARMACIST/SELLER users
export const BROADCAST_NEW_PROMOTION = gql`
  mutation BroadcastNewPromotion($promotionId: ID!, $promotionName: String!) {
    broadcastNewPromotion(promotionId: $promotionId, promotionName: $promotionName)
  }
`;

// Delete notification
export const DELETE_NOTIFICATION = gql`
  mutation DeleteNotification($notificationId: ID!) {
    deleteNotification(notificationId: $notificationId)
  }
`;

// Create notification
export const CREATE_NOTIFICATION = gql`
  mutation CreateNotification($createNotificationInput: CreateNotificationInput!) {
    createNotification(createNotificationInput: $createNotificationInput) {
      id
      notificationType
      message
      date
      seen
      userId
      createdAt
    }
  }
`;

// ================================
// API FUNCTIONS
// ================================

const notificationApi = {
  // Get admin notifications
  getAdminNotifications: async (limit = 50, offset = 0, filter = 'all') => {
    try {
      const { data } = await client.query({
        query: GET_ADMIN_NOTIFICATIONS,
        variables: { limit, offset, filter },
        fetchPolicy: 'network-only'
      });
      return data.adminNotifications;
    } catch (error) {
      console.error('Get admin notifications error:', error);
      throw error;
    }
  },

  // Get laboratory notifications
  getLaboratoryNotifications: async (laboratoryId, limit = 50, offset = 0, filter = 'all') => {
    try {
      const { data } = await client.query({
        query: GET_LABORATORY_NOTIFICATIONS,
        variables: { laboratoryId, limit, offset, filter },
        fetchPolicy: 'network-only'
      });
      return data.laboratoryNotifications;
    } catch (error) {
      console.error('Get laboratory notifications error:', error);
      throw error;
    }
  },

  // Get user notifications
  getUserNotifications: async (userId, limit = 50, offset = 0, filter = 'all') => {
    try {
      const { data } = await client.query({
        query: GET_USER_NOTIFICATIONS,
        variables: { userId, limit, offset, filter },
        fetchPolicy: 'network-only'
      });
      return data.userNotifications;
    } catch (error) {
      console.error('Get user notifications error:', error);
      throw error;
    }
  },

  // Get notification counts
  getNotificationCounts: async (userId) => {
    try {
      const { data } = await client.query({
        query: GET_NOTIFICATION_COUNTS,
        variables: { userId },
        fetchPolicy: 'network-only'
      });
      return data.notificationCounts;
    } catch (error) {
      console.error('Get notification counts error:', error);
      throw error;
    }
  },

  // Mark notification as seen
  markAsRead: async (notificationId) => {
    try {
      const { data } = await client.mutate({
        mutation: MARK_NOTIFICATION_SEEN,
        variables: { notificationId }
      });
      return data.markNotificationSeen;
    } catch (error) {
      console.error('Mark notification seen error:', error);
      throw error;
    }
  },

  // Alias used by Notifications.jsx components
  markNotificationSeen: async (notificationId) => {
    try {
      const { data } = await client.mutate({
        mutation: MARK_NOTIFICATION_SEEN,
        variables: { notificationId }
      });
      return data.markNotificationSeen;
    } catch (error) {
      console.error('Mark notification seen error:', error);
      throw error;
    }
  },

  // Mark all notifications as seen
  markAllAsRead: async (userId) => {
    try {
      const { data } = await client.mutate({
        mutation: MARK_ALL_NOTIFICATIONS_SEEN,
        variables: { userId }
      });
      return data.markAllNotificationsSeen;
    } catch (error) {
      console.error('Mark all notifications seen error:', error);
      throw error;
    }
  },

  // Alias used by Notifications.jsx components
  markAllNotificationsSeen: async (userId) => {
    try {
      const { data } = await client.mutate({
        mutation: MARK_ALL_NOTIFICATIONS_SEEN,
        variables: { userId }
      });
      return data.markAllNotificationsSeen;
    } catch (error) {
      console.error('Mark all notifications seen error:', error);
      throw error;
    }
  },

  // Delete notification
  deleteNotification: async (notificationId) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_NOTIFICATION,
        variables: { notificationId }
      });
      return data.deleteNotification;
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  },

  // Create notification
  createNotification: async (notificationData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_NOTIFICATION,
        variables: { createNotificationInput: notificationData }
      });
      return data.createNotification;
    } catch (error) {
      console.error('Create notification error:', error);
      throw error;
    }
  },

  // Utility functions for notification types
  getNotificationTypes: () => ({
    // Admin notification types
    USER_REGISTRATION: 'USER_REGISTRATION',
    PAYMENT_REQUEST: 'PAYMENT_REQUEST',
    LABORATORY_ACTIVITY: 'LABORATORY_ACTIVITY',
    CONTRACT_ALERT: 'CONTRACT_ALERT',
    SYSTEM_ALERT: 'SYSTEM_ALERT',
    
    // Laboratory notification types
    PRODUCT_APPROVED: 'PRODUCT_APPROVED',
    PRODUCT_REJECTED: 'PRODUCT_REJECTED',
    PRODUCT_PERFORMANCE: 'PRODUCT_PERFORMANCE',
    PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
    PAYMENT_DENIED: 'PAYMENT_DENIED',
    PROMOTION_EXPIRING: 'PROMOTION_EXPIRING',
    PROMOTION_APPROVED: 'PROMOTION_APPROVED',
    CONTRACT_RENEWAL: 'CONTRACT_RENEWAL',
    CONTRACT_EXPIRED: 'CONTRACT_EXPIRED',
    SALES_REPORT: 'SALES_REPORT',
    MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
    
    // General notification types
    TICKET_CREATED: 'TICKET_CREATED',
    TICKET_UPDATED: 'TICKET_UPDATED',
    TICKET_RESOLVED: 'TICKET_RESOLVED'
  })
};

export default notificationApi;